/* eslint-disable */
import { EventBus } from './EventBus'
import { OpenTokAdapter } from '../adapters/OpenTokAdapter'
import { NSFWDetector } from '../nsfw/NSFWDetector'
import { CanvasNSFWFallback } from '../fallback/CanvasNSFWFallback'
import { StatsCollector } from '../utils/stats'
import type {
  ActiveFilter,
  BackgroundBlurConfig,
  ColorFilterConfig,
  IBaseAdapter,
  NSFWDetectionConfig,
  ProcessorStats,
  SupportInfo,
  VideoBackgroundConfig,
  VideoProcessorConfig,
  VideoProcessorEvents,
  VirtualBackgroundConfig
} from './types'
import type { Handler } from 'mitt'

/**
 * Central orchestrator for the `@ocular/video-processor` plugin.
 *
 * Coordinates an {@link IBaseAdapter} (e.g. {@link OpenTokAdapter}),
 * background filters, color filters, NSFW detection, performance metrics
 * and an event bus. Consumers interact exclusively with this class; the
 * adapters, transformers and fallbacks are considered internal.
 *
 * @example
 * ```ts
 * const vp = new VideoProcessor({ adapter: 'opentok' })
 * await vp.attach(publisher)
 * await vp.enableBackgroundBlur({ radius: 'high' })
 * vp.on('nsfw:detected', (data) => console.warn(data))
 * ```
 */
export class VideoProcessor {
  private adapter: IBaseAdapter
  private eventBus: EventBus
  private stats: StatsCollector
  private attached = false
  private publisher: any = null

  private nsfwDetector: NSFWDetector | null = null
  private nsfwFallback: CanvasNSFWFallback | null = null
  private nsfwEnabled = false
  private nsfwConfig: NSFWDetectionConfig | null = null

  /**
   * Create a new VideoProcessor instance.
   *
   * @param config - Configuration object. Pass `{ adapter: 'opentok' }`
   *                 for the built-in OpenTok adapter, or supply a custom
   *                 {@link IBaseAdapter} instance.
   * @throws If an unknown adapter name is provided.
   */
  constructor (config: VideoProcessorConfig) {
    this.eventBus = new EventBus()
    this.stats = new StatsCollector()

    if (typeof config.adapter === 'string') {
      switch (config.adapter) {
        case 'opentok':
          this.adapter = new OpenTokAdapter()
          break
        default:
          throw new Error(`Unknown adapter: ${config.adapter}`)
      }
    } else {
      this.adapter = config.adapter
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  /**
   * Attach the processor to an OpenTok publisher (or provider-specific
   * publisher object). This wires the internal media-processing pipeline
   * and starts performance monitoring.
   *
   * If NSFW detection was enabled before attach, monitoring begins
   * automatically once the publisher is connected.
   *
   * @param publisher - The OpenTok `OT.Publisher` instance (after stream
   *                    creation).
   * @throws If the adapter fails to connect to the publisher.
   */
  async attach (publisher: any): Promise<void> {
    try {
      await this.adapter.attachToPublisher(publisher)
      this.publisher = publisher
      this.attached = true

      this.stats.start((fps, dropped) => {
        this.eventBus.emit('performance:warning', { fps, dropped })
      })

      this.eventBus.emit('ready')

      if (this.nsfwEnabled && this.nsfwDetector) {
        this.startNSFWMonitoring()
      }
    } catch (e: any) {
      this.eventBus.emit('error', {
        code: 'ATTACH_FAILED',
        message: e.message || 'Failed to attach to publisher',
        detail: e
      })
      throw e
    }
  }

  /**
   * Detach the processor from the current publisher. Stops NSFW
   * monitoring and performance collection, and disconnects the adapter.
   */
  async detach (): Promise<void> {
    try {
      this.stopNSFWMonitoring()
      this.stats.stop()
      await this.adapter.detachFromPublisher()
      this.publisher = null
      this.attached = false
    } catch (e: any) {
      this.eventBus.emit('error', {
        code: 'DETACH_FAILED',
        message: e.message || 'Failed to detach from publisher',
        detail: e
      })
    }
  }

  // ─── Background Filters ──────────────────────────────────────────

  /**
   * Enable Gaussian blur on the publisher's background.
   *
   * Requires Insertable Streams, OffscreenCanvas and WebGL. On
   * unsupported browsers an error event is emitted.
   *
   * @param config - Blur radius as `'low'`, `'high'`, or a numeric pixel value.
   * @throws If the adapter cannot apply blur.
   */
  async enableBackgroundBlur (config: BackgroundBlurConfig): Promise<void> {
    try {
      const previousType = this.adapter.getActiveBackgroundType()
      await this.adapter.enableBackgroundBlur(config)

      const eventType = previousType ? 'filter:changed' : 'filter:applied'
      this.eventBus.emit(eventType, { type: 'blur', config: config as any })
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'blur', error: e })
      throw e
    }
  }

  /**
   * Replace the publisher's background with a static image.
   *
   * @param config - Object with `imageUrl` pointing to the background
   *                 image (CORS-accessible).
   * @throws If the adapter cannot set the virtual background.
   */
  async setVirtualBackground (config: VirtualBackgroundConfig): Promise<void> {
    try {
      const previousType = this.adapter.getActiveBackgroundType()
      await this.adapter.setVirtualBackground(config)

      const eventType = previousType ? 'filter:changed' : 'filter:applied'
      this.eventBus.emit(eventType, { type: 'virtual', config: config as any })
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'virtual', error: e })
      throw e
    }
  }

  /**
   * Replace the publisher's background with a looping video.
   *
   * @param config - Object with `videoUrl` pointing to the background
   *                 video (CORS-accessible).
   * @throws If the adapter cannot set the video background.
   */
  async setVideoBackground (config: VideoBackgroundConfig): Promise<void> {
    try {
      const previousType = this.adapter.getActiveBackgroundType()
      await this.adapter.setVideoBackground(config)

      const eventType = previousType ? 'filter:changed' : 'filter:applied'
      this.eventBus.emit(eventType, { type: 'video', config: config as any })
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'video', error: e })
      throw e
    }
  }

  /**
   * Remove any active background filter (blur, virtual, or video).
   * If no background filter is active this is a no-op.
   */
  async disableBackground (): Promise<void> {
    try {
      const previousType = this.adapter.getActiveBackgroundType()
      await this.adapter.disableBackground()

      if (previousType) {
        this.eventBus.emit('filter:removed', { type: previousType })
      }
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'background', error: e })
      throw e
    }
  }

  // ─── Color Filters ─────────────────────────────────────────────────

  /**
   * Apply color adjustments to the publisher's video stream.
   *
   * When Insertable Streams are available (and no background filter
   * occupies the pipeline), adjustments are applied through the
   * {@link ColorFilterTransformer} so remote participants also see them.
   * Otherwise a CSS-only fallback is used (local display only).
   *
   * @param config - Color adjustment values. All fields are optional;
   *                 defaults are neutral (brightness=1, contrast=1, etc.).
   * @throws If the adapter cannot apply the filter.
   */
  async applyColorFilter (config: ColorFilterConfig): Promise<void> {
    try {
      const wasActive = this.adapter.getActiveColorConfig() !== null
      await this.adapter.applyColorFilter(config)

      const eventType = wasActive ? 'filter:changed' : 'filter:applied'
      this.eventBus.emit(eventType, { type: 'color', config: config as any })
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'color', error: e })
      throw e
    }
  }

  /**
   * Remove color filters, restoring the original video appearance.
   * If no color filter is active this is a no-op.
   */
  async removeColorFilter (): Promise<void> {
    try {
      const wasActive = this.adapter.getActiveColorConfig() !== null
      await this.adapter.removeColorFilter()

      if (wasActive) {
        this.eventBus.emit('filter:removed', { type: 'color' })
      }
    } catch (e: any) {
      this.eventBus.emit('filter:error', { type: 'color', error: e })
      throw e
    }
  }

  // ─── NSFW Detection ─────────────────────────────────────────────

  /**
   * Enable client-side NSFW content detection on the publisher's video.
   *
   * Lazy-loads the `nsfwjs` model (~30 MB for InceptionV3 default) and
   * starts sampling frames at the configured interval. Detection events
   * are emitted via the `nsfw:detected` event only after consecutive
   * frames confirm explicit content and the cooldown has elapsed.
   *
   * @param config - Detection options (interval, threshold, model, etc.).
   * @throws If the model cannot be loaded.
   */
  async enableNSFWDetection (config: NSFWDetectionConfig): Promise<void> {
    try {
      this.nsfwConfig = config

      this.nsfwDetector = new NSFWDetector(config, {
        onDetection: (result) => this.eventBus.emit('nsfw:detected', result),
        onError: (error) => this.eventBus.emit('nsfw:error', { error }),
        onStatus: (status) => {
          if (status === 'loading') this.eventBus.emit('nsfw:model:loading')
          if (status === 'ready') this.eventBus.emit('nsfw:model:ready')
        }
      })

      await this.nsfwDetector.loadModel()
      this.nsfwEnabled = true

      if (this.attached && this.publisher) {
        this.startNSFWMonitoring()
      }
    } catch (e: any) {
      this.eventBus.emit('nsfw:error', { error: e })
      throw e
    }
  }

  /**
   * Reset NSFW detection state (clear sliding-window buffer and
   * cooldown timer) so the next explicit frame can trigger a new
   * detection immediately. Call this after an operator acknowledges
   * a previous detection (e.g. "Continue session").
   */
  resetNSFWDetectionState (): void {
    if (this.nsfwDetector) {
      this.nsfwDetector.resetDetectionState()
    }
  }

  /**
   * Disable NSFW detection and dispose the loaded model.
   * Emits `nsfw:cleared` once stopped.
   */
  async disableNSFWDetection (): Promise<void> {
    this.stopNSFWMonitoring()

    if (this.nsfwDetector) {
      await this.nsfwDetector.dispose()
      this.nsfwDetector = null
    }

    this.nsfwEnabled = false
    this.nsfwConfig = null
    this.eventBus.emit('nsfw:cleared')
  }

  // ─── Events ──────────────────────────────────────────────────────

  /**
   * Subscribe to a VideoProcessor event.
   *
   * @param event   - Event name (e.g. `'ready'`, `'nsfw:detected'`).
   * @param handler - Callback invoked when the event fires.
   */
  on<K extends keyof VideoProcessorEvents> (
    event: K,
    handler: Handler<VideoProcessorEvents[K]>
  ): void {
    this.eventBus.on(event, handler)
  }

  /**
   * Unsubscribe from a VideoProcessor event.
   *
   * @param event   - Event name.
   * @param handler - The exact handler reference passed to {@link on}.
   */
  off<K extends keyof VideoProcessorEvents> (
    event: K,
    handler: Handler<VideoProcessorEvents[K]>
  ): void {
    this.eventBus.off(event, handler)
  }

  // ─── Utils ───────────────────────────────────────────────────────

  /**
   * Query browser support for every feature the processor offers.
   *
   * Returns a {@link SupportInfo} object with per-feature booleans and
   * a {@link BrowserInfo} descriptor.
   */
  isSupported (): SupportInfo {
    return this.adapter.isSupported()
  }

  /**
   * Get a snapshot of current performance metrics.
   *
   * Includes FPS, dropped frames, NSFW analysis statistics, average
   * frame processing time, and uptime.
   */
  getStats (): ProcessorStats {
    return this.stats.getSnapshot(
      this.nsfwDetector ? this.nsfwDetector.getAnalysisCount() : 0,
      this.nsfwDetector ? this.nsfwDetector.getAvgLatencyMs() : 0,
      this.getActiveFilters().length
    )
  }

  /**
   * List every filter currently active on the processor.
   *
   * @returns Array of {@link ActiveFilter} with type and config for each.
   */
  getActiveFilters (): ActiveFilter[] {
    const filters: ActiveFilter[] = []
    const bgType = this.adapter.getActiveBackgroundType()

    if (bgType) {
      filters.push({ type: bgType, config: {} })
    }

    const colorConfig = this.adapter.getActiveColorConfig()
    if (colorConfig) {
      filters.push({ type: 'color', config: colorConfig as any })
    }

    if (this.nsfwEnabled && this.nsfwConfig) {
      filters.push({ type: 'nsfw', config: this.nsfwConfig as any })
    }

    return filters
  }

  /**
   * Whether the processor is currently attached to a publisher.
   */
  isAttached (): boolean {
    return this.attached
  }

  /**
   * Get a reference to the internal {@link StatsCollector}.
   * Useful for recording frame timings from external transformers.
   */
  getStatsCollector (): StatsCollector {
    return this.stats
  }

  /**
   * Destroy the processor: disable NSFW, tear down the adapter, stop
   * stats, and clear all event listeners. The instance should not be
   * reused after calling this.
   */
  async destroy (): Promise<void> {
    try {
      if (this.nsfwEnabled) {
        await this.disableNSFWDetection()
      }
      this.stats.destroy()
      if (this.attached) {
        await this.detach()
      }
      await this.adapter.destroy()
      this.eventBus.emit('destroyed')
      this.eventBus.removeAllListeners()
    } catch (e: any) {
      this.eventBus.emit('error', {
        code: 'DESTROY_FAILED',
        message: e.message || 'Failed to destroy processor',
        detail: e
      })
    }
  }

  // ─── Private: NSFW monitoring ──────────────────────────────────

  private startNSFWMonitoring (): void {
    if (!this.nsfwDetector || !this.publisher) return

    const videoEl = this.getPublisherVideoElement()
    if (!videoEl) return

    const interval = this.nsfwConfig?.sampleIntervalMs ?? 1500
    this.nsfwFallback = new CanvasNSFWFallback(this.nsfwDetector, interval)
    this.nsfwFallback.start(videoEl)
  }

  private stopNSFWMonitoring (): void {
    if (this.nsfwFallback) {
      this.nsfwFallback.destroy()
      this.nsfwFallback = null
    }
  }

  private getPublisherVideoElement (): HTMLVideoElement | null {
    if (!this.publisher) return null
    const container = this.publisher.element || this.publisher.targetElement
    if (container && container.querySelector) {
      return container.querySelector('video') as HTMLVideoElement || null
    }
    return null
  }
}
